import './PrivacyPolicy.css'

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: January 22, 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to UniqueBrains ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our online learning platform at uniquebrains.org.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>We collect information that you provide directly to us, including:</p>
          <ul>
            <li>Name, email address, and contact information</li>
            <li>Account credentials (username and password)</li>
            <li>Profile information (age, grade level, learning preferences)</li>
            <li>Payment and billing information</li>
            <li>Communications with us</li>
          </ul>

          <h3>2.2 Student Information</h3>
          <p>For student accounts, we collect:</p>
          <ul>
            <li>Student names and ages</li>
            <li>Grade levels and educational information</li>
            <li>Neurodiversity profiles and learning accommodations</li>
            <li>Course enrollment and progress data</li>
          </ul>

          <h3>2.3 Automatically Collected Information</h3>
          <p>When you use our platform, we automatically collect:</p>
          <ul>
            <li>Device information (browser type, operating system)</li>
            <li>Usage data (pages visited, features used, time spent)</li>
            <li>IP address and location data</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our educational services</li>
            <li>Process enrollments and manage course access</li>
            <li>Facilitate communication between students and instructors</li>
            <li>Personalize learning experiences based on individual needs</li>
            <li>Process payments and prevent fraud</li>
            <li>Send important updates, notifications, and educational content</li>
            <li>Analyze usage patterns to improve our platform</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Information Sharing and Disclosure</h2>
          
          <h3>4.1 With Your Consent</h3>
          <p>We may share your information with third parties when you give us explicit consent to do so.</p>

          <h3>4.2 Service Providers</h3>
          <p>We share information with trusted service providers who assist us in operating our platform, including:</p>
          <ul>
            <li>Cloud hosting services (Supabase)</li>
            <li>Payment processors</li>
            <li>Email service providers</li>
            <li>Analytics providers</li>
          </ul>

          <h3>4.3 Instructors</h3>
          <p>Course instructors have access to enrolled student information necessary to provide educational services, including names, progress data, and submitted assignments.</p>

          <h3>4.4 Legal Requirements</h3>
          <p>We may disclose information if required by law or in response to valid legal requests.</p>

          <h3>4.5 What We Don't Do</h3>
          <p>We do NOT:</p>
          <ul>
            <li>Sell your personal information to third parties</li>
            <li>Use student data for advertising purposes</li>
            <li>Share information with unauthorized parties</li>
          </ul>
        </section>

        <section>
          <h2>5. Children's Privacy</h2>
          <p>
            UniqueBrains is designed to serve students of all ages, including children under 13. We comply with the Children's Online Privacy Protection Act (COPPA) and similar regulations:
          </p>
          <ul>
            <li>We require parental consent before collecting information from users under 18</li>
            <li>We collect only the minimum information necessary to provide educational services</li>
            <li>Student data is never used for advertising or marketing purposes</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your information, including:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication and access controls</li>
            <li>Regular security audits and updates</li>
            <li>Row-level security policies in our database</li>
          </ul>
          <p>
            However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          
          <h3>7.1 Access and Update</h3>
          <p>You can access and update your account information at any time through your profile settings.</p>

          <h3>7.2 Data Deletion</h3>
          <p>You can request deletion of your account and associated data by contacting us at privacy@uniquebrains.org.</p>

          <h3>7.3 Marketing Communications</h3>
          <p>You can opt out of marketing emails by clicking the unsubscribe link in any promotional email.</p>

          <h3>7.4 Cookies</h3>
          <p>You can control cookies through your browser settings, though some features may not function properly if cookies are disabled.</p>
        </section>

        <section>
          <h2>8. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or as needed to provide services. After account deletion, we may retain certain information for legal compliance, dispute resolution, and legitimate business purposes.
          </p>
        </section>

        <section>
          <h2>9. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Links</h2>
          <p>
            Our platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our platform after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us:</p>
          <ul>
            <li>Email: privacy@uniquebrains.org</li>
            <li>Website: https://uniquebrains.org</li>
            <li>Instagram: @ouruniquebrains</li>
          </ul>
        </section>

        <section>
          <h2>13. California Privacy Rights</h2>
          <p>
            California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, the right to delete personal information, and the right to opt-out of the sale of personal information. We do not sell personal information.
          </p>
        </section>

        <section>
          <h2>14. GDPR Compliance</h2>
          <p>
            For users in the European Economic Area (EEA), we comply with the General Data Protection Regulation (GDPR). You have the right to access, rectify, erase, restrict processing, data portability, and object to processing of your personal data.
          </p>
        </section>
      </div>
    </div>
  )
}

export default PrivacyPolicy
